import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import 'react-toastify/dist/ReactToastify.css';
import './styles/App.css';
import './styles/theme.css';
import './styles/mobile.css';

// i18next is initialized in src/i18n.js (imported from src/index.js).
// Do NOT import/init i18next here again — that was causing double-init and
// a "changeLanguage is not a function" crash on the admin route.

import FloatingElements from './components/FloatingElements';
import ScrollToTop from './components/ScrollToTop';
import AIStyleChatbot from './components/AIStyleChatbot';
import Header from './components/Header';
// Apr28 v14 — affiliate transparency: shows when customer has active referral cookie
import ReferralBanner from './components/ReferralBanner';
// Apr28 v17 — smart push notification permission prompt (engagement-gated)
import PushPermissionPrompt from './components/PushPermissionPrompt';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import InstallAppButton from './components/InstallAppButton';
import Home from './pages/Home';
// Apr28 v11 — captures ?ref=CODE on any URL into localStorage with 30-day expiry
import { initRefAttribution } from './utils/refAttribution';

// Lazy-loaded pages (Fix #6: performance)
const Shop          = lazy(() => import('./pages/Shop'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart          = lazy(() => import('./pages/Cart'));
const Checkout      = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const UserAccount   = lazy(() => import('./pages/UserAccount'));
const Wishlist      = lazy(() => import('./pages/Wishlist'));
const Login         = lazy(() => import('./pages/Login'));
const Register      = lazy(() => import('./pages/Register'));
const NotFound      = lazy(() => import('./pages/NotFound'));
const BlogsList     = lazy(() => import('./pages/BlogsList'));
const BlogDetail    = lazy(() => import('./pages/BlogDetail'));
const ShopByVideo   = lazy(() => import('./pages/ShopByVideo'));
const AboutJyotiPage = lazy(() => import('./components/AboutJyotiPage'));
const PolicyPage    = lazy(() => import('./pages/PolicyPage')); // Fix #3
// Apr28 v12 — creator storefront + collection pages
const CreatorStorefront = lazy(() => import('./pages/CreatorStorefront'));
const CollectionPage    = lazy(() => import('./pages/CollectionPage'));

// Admin pages
const AdminDashboard      = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts       = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders         = lazy(() => import('./pages/admin/AdminOrders'));
const AdminCustomers      = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminCategories     = lazy(() => import('./pages/admin/AdminCategories'));
const AdminCoupons        = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminLoyalty        = lazy(() => import('./pages/admin/AdminLoyalty'));
const AdminCreators       = lazy(() => import('./pages/admin/AdminCreators'));
const AdminPayouts        = lazy(() => import('./pages/admin/AdminPayouts'));
const BecomeCreator       = lazy(() => import('./pages/BecomeCreator'));
const ReferralLanding     = lazy(() => import('./pages/ReferralLanding'));
const CreatorDashboard    = lazy(() => import('./pages/CreatorDashboard'));
const AdminReviews        = lazy(() => import('./pages/admin/AdminReviews'));
const AdminBlogs          = lazy(() => import('./pages/admin/AdminBlogs'));
const AdminVideos         = lazy(() => import('./pages/admin/AdminVideos'));
const AdminBanner         = lazy(() => import('./pages/admin/AdminBanner'));
const AdminHeroBanner     = lazy(() => import('./pages/admin/AdminHeroBanner'));
const AdminPages          = lazy(() => import('./pages/admin/AdminPages'));
const AdminAnalytics      = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminSettings       = lazy(() => import('./pages/admin/AdminSettings'));
// Apr28 v16 — dedicated About Jyoti editor
const AdminAboutJyoti     = lazy(() => import('./pages/admin/AdminAboutJyoti'));
const AdminNotifications  = lazy(() => import('./pages/admin/AdminNotifications'));
// Apr29 v28 — returns admin page
const AdminReturns        = lazy(() => import('./pages/admin/AdminReturns'));
const AdminUsers          = lazy(() => import('./pages/admin/AdminUsers'));
const AdminPaymentGateway = lazy(() => import('./pages/admin/AdminPaymentGateway'));
const AdminShipping       = lazy(() => import('./pages/admin/AdminShipping'));
const AdminWhatsApp       = lazy(() => import('./pages/admin/AdminWhatsApp')); // Feature #9
const AdminSEO            = lazy(() => import('./pages/admin/AdminSEO'));
const AdminCRM            = lazy(() => import('./pages/admin/AdminCRM'));
const AdminInventory      = lazy(() => import('./pages/admin/AdminInventory'));
const AdminPOS            = lazy(() => import('./pages/admin/AdminPOS'));
const AdminBarcodes       = lazy(() => import('./pages/admin/AdminBarcodes'));
const AdminAIUpload       = lazy(() => import('./pages/admin/AdminAIUpload'));
const AdminAICaptions     = lazy(() => import('./pages/admin/AdminAICaptions'));
const AdminContentStudio  = lazy(() => import('./pages/admin/AdminContentStudio'));
const AdminGrowthEngine   = lazy(() => import('./pages/admin/AdminGrowthEngine'));
const AdminEmailCampaigns = lazy(() => import('./pages/admin/AdminEmailCampaigns'));
const AdminWhatsAppCampaigns = lazy(() => import('./pages/admin/AdminWhatsAppCampaigns'));
const AdminCompetitors    = lazy(() => import('./pages/admin/AdminCompetitors'));

const PageLoader = () => (
  <div style={{ minHeight:'60vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
    <div style={{ textAlign:'center' }}>
      <div style={{ fontSize:32, marginBottom:16 }}>💎</div>
      <div style={{ color:'#c9a84c', fontSize:13, letterSpacing:3 }}>LOADING</div>
    </div>
  </div>
);

const Layout = ({ children }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  return (
    <>
      {!isAdmin && <FloatingElements />}
      {!isAdmin && <Header />}
      {!isAdmin && <ReferralBanner />}
      <main className={isAdmin ? '' : 'main-content'} style={{ position:'relative', zIndex:2 }}>
        {children}
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <WhatsAppButton />}
      {!isAdmin && <AIStyleChatbot />}
      {!isAdmin && <PWAInstallPrompt />}
      {!isAdmin && <InstallAppButton />}
      {/* Apr28 v17 — push permission prompt (engagement-gated, shows after 2nd visit) */}
      {!isAdmin && <PushPermissionPrompt />}
    </>
  );
};

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.title = 'Glitz & Glam by Jyoti — Premium Artificial Jewelry & Fashion';
    const saved = localStorage.getItem('language') || 'en';
    // Defensive: only call if i18n is fully initialized AND exposes changeLanguage
    if (i18n && typeof i18n.changeLanguage === 'function') {
      try {
        i18n.changeLanguage(saved);
      } catch (err) {
        console.warn('i18n.changeLanguage failed:', err);
      }
    }

    // Preload wishlist IDs (Apr28 v6) — populates sessionStorage cache
    // so PremiumProductCard hearts know their state instantly without
    // each card pinging the API. Silent fail if not logged in or offline.
    const token = localStorage.getItem('token');
    if (token) {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.glitzandglambyjyoti.com';
      fetch(`${apiUrl}/api/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(items => {
          const ids = (Array.isArray(items) ? items : []).map(it => it.productId?._id || it.productId).filter(Boolean);
          try { sessionStorage.setItem('gg_wishlist_ids', JSON.stringify(ids)); } catch (_) {}
        })
        .catch(() => {});

      // Apr28 v11 — preload creator status so PremiumProductCard can show
      // the "🔗 Share with my code" button for approved creators.
      fetch(`${apiUrl}/api/creator/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d?.user?.creatorStatus === 'approved' && d?.user?.creatorCode) {
            try {
              sessionStorage.setItem('gg_creator', JSON.stringify({
                code: d.user.creatorCode,
                rate: d.user.commissionRate || 10,
              }));
            } catch (_) {}
          } else {
            try { sessionStorage.removeItem('gg_creator'); } catch (_) {}
          }
        })
        .catch(() => {});
    }

    // Apr28 v11 — capture ?ref=CODE on any URL (creator affiliate link).
    // Was a silent no-op before because initRefAttribution was never called.
    // Now: if URL has ?ref=PRIYA123, stored 30 days, used at checkout.
    initRefAttribution();

    // DARK is default — only apply pink if user explicitly chose it THIS session
    const theme = localStorage.getItem('site_theme');
    if (theme === 'pink') {
      document.documentElement.classList.add('theme-pink');
    } else {
      // Always ensure dark theme is default
      document.documentElement.classList.remove('theme-pink');
      localStorage.setItem('site_theme', 'dark');
    }
  }, [i18n]);

  return (
    <Router>
      <ScrollToTop />
      <Layout>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/"                   element={<Home />} />
            <Route path="/shop"               element={<Shop />} />
            <Route path="/products"           element={<Shop />} />
            <Route path="/product/:id"        element={<ProductDetail />} />
            <Route path="/cart"               element={<Cart />} />
            <Route path="/checkout"           element={<Checkout />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
            <Route path="/account"            element={<UserAccount />} />
            <Route path="/wishlist"           element={<Wishlist />} />
            <Route path="/login"              element={<Login />} />
            <Route path="/register"           element={<Register />} />
            <Route path="/blogs"              element={<BlogsList />} />
            <Route path="/blog/:id"           element={<BlogDetail />} />
            <Route path="/shop-by-video"      element={<ShopByVideo />} />
            <Route path="/about-jyoti"        element={<AboutJyotiPage />} />
            {/* Apr28 v12 — creator storefronts (link-only, noindex) */}
            <Route path="/creator/:code"      element={<CreatorStorefront />} />
            <Route path="/c/:code/:slug"      element={<CollectionPage />} />
            {/* Fix #3 — Policy pages were 404 */}
            <Route path="/pages/:slug"        element={<PolicyPage />} />

            <Route path="/admin"                   element={<AdminDashboard />} />
            <Route path="/admin/products"          element={<AdminProducts />} />
            <Route path="/admin/orders"            element={<AdminOrders />} />
            <Route path="/admin/customers"         element={<AdminCustomers />} />
            <Route path="/admin/categories"        element={<AdminCategories />} />
            <Route path="/admin/coupons"           element={<AdminCoupons />} />
            <Route path="/admin/loyalty"           element={<AdminLoyalty />} />
            <Route path="/admin/creators"          element={<AdminCreators />} />
            <Route path="/admin/payouts"           element={<AdminPayouts />} />
            <Route path="/become-creator"          element={<BecomeCreator />} />
            <Route path="/creator/dashboard"       element={<CreatorDashboard />} />
            <Route path="/ref/:code"               element={<ReferralLanding />} />
            <Route path="/admin/reviews"           element={<AdminReviews />} />
            <Route path="/admin/blogs"             element={<AdminBlogs />} />
            <Route path="/admin/videos"            element={<AdminVideos />} />
            <Route path="/admin/banner"            element={<AdminBanner />} />
            <Route path="/admin/hero-banner"       element={<AdminHeroBanner />} />
            <Route path="/admin/pages"             element={<AdminPages />} />
            <Route path="/admin/analytics"         element={<AdminAnalytics />} />
            <Route path="/admin/settings"          element={<AdminSettings />} />
            <Route path="/admin/about-jyoti"        element={<AdminAboutJyoti />} />
            <Route path="/admin/returns"            element={<AdminReturns />} />
            <Route path="/admin/notifications"      element={<AdminNotifications />} />
            <Route path="/admin/notifications"     element={<AdminNotifications />} />
            <Route path="/admin/users"             element={<AdminUsers />} />
            <Route path="/admin/payments"          element={<AdminPaymentGateway />} />
            <Route path="/admin/shipping"          element={<AdminShipping />} />
            <Route path="/admin/whatsapp"          element={<AdminWhatsApp />} />
            <Route path="/admin/seo"               element={<AdminSEO />} />
            <Route path="/admin/crm"               element={<AdminCRM />} />
            <Route path="/admin/inventory"          element={<AdminInventory />} />
            <Route path="/admin/pos"                element={<AdminPOS />} />
            <Route path="/admin/barcodes"           element={<AdminBarcodes />} />
            <Route path="/admin/ai-upload"          element={<AdminAIUpload />} />
            <Route path="/admin/ai-captions"        element={<AdminAICaptions />} />
            <Route path="/admin/content-studio"      element={<AdminContentStudio />} />
            <Route path="/admin/growth-engine"       element={<AdminGrowthEngine />} />
            <Route path="/admin/campaigns-email"     element={<AdminEmailCampaigns />} />
            <Route path="/admin/campaigns-whatsapp"  element={<AdminWhatsAppCampaigns />} />
            <Route path="/admin/competitors"         element={<AdminCompetitors />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Layout>
      <ToastContainer position="bottom-right" autoClose={3000} theme="dark"/>
    </Router>
  );
}

export default App;
